import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ShieldCheck, Sparkles, Clock, ListChecks } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { homeFor } from "../components/ProtectedRoute";
import DemoPreview from "../components/DemoPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Landing page — VI Business Academy light theme. White canvas, Aubergine for
// headings + active surfaces, Crimson for CTAs, Mustard for accents. The dark
// dashboard experience begins post-login.

export default function LandingPage() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    axios.get(`${API}/programs`).then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  const ctaTo = user ? homeFor(user.role) : "/login";
  const ctaLabel = user ? "Go to your dashboard" : "Log in to begin";

  return (
    <div className="min-h-screen bg-white text-[#1A1A24]" data-testid="landing-page">
      {/* Nav — white with subtle aubergine bottom rule */}
      <nav className="border-b border-[#E4E5EB] bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src="/brand/vi-academy-logo-light.png" alt="Vi Business Academy" className="h-14 w-auto object-contain" />
            <div className="hidden sm:flex flex-col leading-tight pl-3 border-l border-[#E4E5EB]">
              <span className="text-[8px] uppercase tracking-[0.18em] text-[#8A8B9D] font-semibold">Powered by</span>
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#5F004B] font-bold">Mercury Compass Assess</span>
            </div>
          </div>
          <Link to={ctaTo} className="btn btn-primary text-xs uppercase tracking-widest !py-2 !px-4" data-testid="nav-login-link">
            {user ? "Dashboard" : "Log in"} <ArrowRight size={12} />
          </Link>
        </div>
      </nav>

      {/* Hero — white bg with soft aubergine + mustard bloom decorations */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#5F004B] opacity-[0.06] blur-3xl pointer-events-none" />
        <div className="absolute top-40 -left-24 w-[360px] h-[360px] rounded-full bg-[#FFC600] opacity-[0.10] blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-24 grid md:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EE2737]/10 border border-[#EE2737]/20 text-[10px] uppercase tracking-widest text-[#EE2737] font-bold mb-6">
              <Sparkles size={12} /> Certification Programme · Vi Business Academy
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.1] tracking-tight text-[#1A1A24]" data-testid="landing-hero-title">
              Vi Business Academy <span className="text-[#5F004B]">Certified Sales Consultant</span>
            </h1>
            <p className="text-[15px] text-[#535465] mt-6 max-w-xl leading-relaxed">
              Congratulations on completing the Consultative Solution Selling Workshop. Through this program, you have built capabilities to better uncover customer needs, engage stakeholders through value-led conversations, and position solutions that address business outcomes.
            </p>
            <p className="text-[15px] text-[#535465] mt-4 max-w-xl leading-relaxed">
              As the next step in your learning journey, you will participate in the <strong className="text-[#1A1A24]">Vi Business Academy - Certified Sales Consultant</strong> certification. The certification comprises three components: a <strong className="text-[#5F004B]">knowledge-based assessment</strong>, a <strong className="text-[#5F004B]">customer sales pitch simulation</strong>, and an <strong className="text-[#5F004B]">AI-led role play</strong> designed to evaluate how effectively you apply consultative selling principles in realistic customer scenarios.
            </p>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <Link to={ctaTo} className="btn btn-primary text-sm px-6 py-3" data-testid="landing-cta-primary">
                {ctaLabel} <ArrowRight size={14} />
              </Link>
              <a href="#how-it-works" className="text-xs uppercase tracking-widest text-[#5F004B] font-bold hover:opacity-70 transition-opacity" data-testid="landing-cta-secondary">
                How it works ↓
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-[#535465] flex-wrap">
              <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-[#5F004B]" /> ID + face verified</div>
              <div className="flex items-center gap-2"><Sparkles size={14} className="text-[#EE2737]" /> AI voice + AI-scored responses</div>
              <div className="flex items-center gap-2"><Clock size={14} className="text-[#FFC600]" style={{ filter: "drop-shadow(0 0 2px rgba(0,0,0,0.15))" }} /> 30 min per attempt</div>
            </div>
          </div>

          {/* Live inline preview — 3 auto-cycling assessment panels */}
          <DemoPreview />
        </div>
      </section>

      {/* Programmes on offer */}
      <section id="how-it-works" className="border-t border-[#E4E5EB] bg-[#F8F8FA]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-[10px] uppercase tracking-widest text-[#EE2737] font-bold mb-3">Programmes on the platform</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black mb-3 text-[#1A1A24]">Currently live for Vodafone Idea</h2>
          <p className="text-sm text-[#535465] max-w-xl mb-10 leading-relaxed">
            Each candidate sees only the programmes they have been invited to. If you are invited to more than one, both appear on your dashboard with their own status.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {programs.map((p) => (
              <div key={p.key} className="border border-[#E4E5EB] bg-white p-6 rounded-md hover:border-[#5F004B] hover:shadow-lg transition-all" data-testid={`landing-program-${p.key}`}>
                <div className="w-10 h-10 mb-4 flex items-center justify-center rounded-md" style={{ background: p.cover_hex }}>
                  <ListChecks size={18} className="text-white" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#8A8B9D] mb-2">{p.duration_min} min · {p.sections.length} sections</div>
                <h3 className="font-heading text-xl font-bold mb-2 text-[#1A1A24]">{p.name}</h3>
                <p className="text-xs text-[#EE2737] font-semibold mb-4">{p.tagline}</p>
                <p className="text-sm text-[#535465] leading-relaxed mb-5">{p.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {p.sections.map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-wider px-2 py-1 bg-[#F4F4F8] border border-[#E4E5EB] text-[#535465] rounded-sm">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-8">
        {[
          { n: "01", h: "Login with your credentials", b: "Your login credentials are mentioned in the invite email sent from Mercury." },
          { n: "02", h: "Verify who you are", b: "A one-time selfie + VI ID check runs before your first attempt. This is stored securely & used only for attempt-integrity." },
          { n: "03", h: "Attempt in one sitting", b: "Each section is timed. AI proctoring flags tab switches, absent faces and phone use. You get one attempt unless VI Business Academy approves a retake." },
        ].map((step) => (
          <div key={step.n} className="border-l-2 border-[#5F004B] pl-5" data-testid={`landing-step-${step.n}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#EE2737] font-bold mb-3">{step.n}</div>
            <div className="font-heading text-lg font-bold mb-2 text-[#1A1A24]">{step.h}</div>
            <div className="text-sm text-[#535465] leading-relaxed">{step.b}</div>
          </div>
        ))}
      </section>

      {/* Footer CTA — soft sand-tinted panel for brand freshness */}
      <section className="border-t border-[#E4E5EB] bg-[#F9E9D0]/40 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-heading text-2xl sm:text-3xl font-black mb-4 text-[#5F004B]">Ready when you are.</h3>
          <p className="text-sm text-[#535465] mb-8 leading-relaxed">Log in with the credentials VI Business Academy sent you. Your programme card appears the moment you sign in.</p>
          <Link to={ctaTo} className="btn btn-primary text-sm px-6 py-3 inline-flex" data-testid="landing-cta-footer">
            {ctaLabel} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <footer className="text-center text-[10px] uppercase tracking-widest text-[#8A8B9D] py-8 border-t border-[#E4E5EB]">
        © 2026 VI Business Academy · Powered by Mercury Compass Assess
      </footer>
    </div>
  );
}
